import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

export type ParentInvitePrefill = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  kidProfileId: string | null;
  kidProfileName: string | null;
  organizationId: string | null;
  invitationId: string | null;
  inviteeRole: string | null;
};

export function splitPersonName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function digitsPhone(raw: string): string {
  return String(raw || '').replace(/\D/g, '').slice(0, 10);
}

function parentDetailsFromKidNotes(notesRaw: string | null | undefined): {
  name: string;
  contact: string;
  email: string;
} {
  if (!notesRaw?.trim()) return { name: '', contact: '', email: '' };
  try {
    const parsed = JSON.parse(notesRaw) as {
      parent?: { name?: string; contact?: string; email?: string };
    };
    const parent = parsed?.parent;
    return {
      name: String(parent?.name || '').trim(),
      contact: String(parent?.contact || '').trim(),
      email: String(parent?.email || '').trim(),
    };
  } catch {
    return { name: '', contact: '', email: '' };
  }
}

async function findInvitationByToken(token: string) {
  const client = generateClient<Schema>();
  const normalized = token.trim();
  if (!normalized) return null;

  const rows: Array<NonNullable<(Awaited<ReturnType<typeof client.models.UserInvitation.list>>)['data']>[number]> =
    [];
  let nextToken: string | null | undefined = undefined;
  do {
    const res = await client.models.UserInvitation.list({
      authMode: 'apiKey',
      filter: { token: { eq: normalized } },
      limit: 100,
      nextToken: nextToken || undefined,
    });
    rows.push(...(res.data ?? []));
    nextToken = res.nextToken ?? null;
  } while (nextToken);

  if (rows.length === 0) return null;

  // Prefer an unused invite if duplicates somehow exist.
  return rows.find((row) => row.status !== 'REGISTERED') || rows[0];
}

/** Load invitation + linked kid notes so parent signup can autofill name/email/phone. */
export async function loadParentInvitePrefill(token: string): Promise<{
  prefill: ParentInvitePrefill | null;
  error: string | null;
}> {
  try {
    const invitation = await findInvitationByToken(token);
    if (!invitation) {
      return {
        prefill: null,
        error:
          'Invitation not found for this link. Open it on the same app where the invite was sent (for local testing use localhost with the same token), and make sure the invite was created successfully.',
      };
    }

    // Only block after the parent account has actually been created from this invite.
    if (invitation.status === 'REGISTERED') {
      return {
        prefill: null,
        error: 'This invitation has already been used to create an account.',
      };
    }

    const client = generateClient<Schema>();
    const email = String(invitation.email || '').toLowerCase().trim();
    let firstName = '';
    let lastName = '';
    let phoneNumber = '';

    if (invitation.kidProfileId) {
      try {
        const kidRes = await client.models.KidProfile.get(
          { id: invitation.kidProfileId },
          { authMode: 'apiKey' },
        );
        const fromNotes = parentDetailsFromKidNotes(kidRes.data?.notes ?? null);
        const split = splitPersonName(fromNotes.name);
        firstName = split.firstName;
        lastName = split.lastName;
        phoneNumber = digitsPhone(fromNotes.contact);
      } catch {
        // non-blocking — still prefill email from invitation
      }
    }

    return {
      prefill: {
        email,
        firstName,
        lastName,
        phoneNumber,
        kidProfileId: invitation.kidProfileId ?? null,
        kidProfileName: invitation.kidProfileName ?? null,
        organizationId: invitation.organizationId ?? null,
        invitationId: invitation.id ?? null,
        inviteeRole: invitation.inviteeRole ?? 'PARENT',
      },
      error: null,
    };
  } catch (e) {
    return {
      prefill: null,
      error:
        e instanceof Error
          ? `Could not load invitation: ${e.message}`
          : 'Could not load invitation. Please try again.',
    };
  }
}

export async function associateInvitedKidToParent(params: {
  invitationToken: string;
  parentUserId: string;
}): Promise<{ kidProfileId: string | null }> {
  const invitation = await findInvitationByToken(params.invitationToken);
  if (!invitation) return { kidProfileId: null };

  const client = generateClient<Schema>();

  if (invitation.inviteeRole === 'PARENT' && invitation.kidProfileId) {
    await client.models.KidProfile.update(
      {
        id: invitation.kidProfileId,
        parentId: params.parentUserId,
        organizationId: invitation.organizationId ?? undefined,
      } as never,
      { authMode: 'apiKey' },
    );
  }

  if (invitation.id && invitation.status !== 'REGISTERED') {
    await client.models.UserInvitation.update(
      { id: invitation.id, status: 'REGISTERED' },
      { authMode: 'apiKey' },
    );
  }

  return { kidProfileId: invitation.kidProfileId ?? null };
}
